<template>
  <div>
    <PageTitle tag="h1" class="mb-7" v-if="!isIntegrationError">
      {{ $t('employersEntrance') }}
    </PageTitle>
    <VRow>
      <VCol
        cols="12"
        sm="6"
        md="4"
        class="mx-auto mb"
      >
        <IntegrationsError v-if="isIntegrationError" />
        <template v-else>
          <VAlert
            v-if="wasRedirectedToRu"
            type="info"
            outlined
            class="mb-5"
          >
            <div class="body-2">
              {{ $t('siteMovedToRu') }}
            </div>
            <div class="body-2 mt-2">
              {{ $t('autoRedirectedMessage') }}
            </div>
          </VAlert>
          <VForm @submit.prevent="auth">
            <div class="mt-sm-12 mt-5">
              <VTextField
                id="login"
                v-model="login"
                type="email"
                :label="$t('email')"
                outlined
                dense
                placeholder=" "
                autocomplete="username"
                :error-messages="errors.login"
              />
              <VTextField
                id="password"
                v-model="password"
                :label="$t('pass')"
                outlined
                dense
                placeholder=" "
                type="password"
                autocomplete="current-password"
                :error-messages="errors.password"
              />
            </div>
            <VRow no-gutters align="center" class="body-1 flex-column">
              <VCheckbox
                v-model="rememberMe"
                hide-details
                row
                class="body-1"
                :label="$t('rememberMe')"
              />
              <div v-if="hasErrorMessageOnly" class="mt-5 red--text text-center">
                {{ errorMessage }}
              </div>
              <VBtn
                id="login-button"
                color="success"
                dark
                elevation="0"
                class="mt-5"
                type="submit"
                :loading="loading"
              >
                {{ $t('login') }}
              </VBtn>
              <a v-if="isDev" :href="authUrl">
                Логин работа (только для dev режима)
              </a>
            </VRow>
          </VForm>
          <VRow
            align="center"
            no-gutters
            justify-md="space-between"
            class="mt-5 flex-column flex-md-row body-1"
          >
            <RouterLink :to="{ path: 'registration', query: { referralCode: referralCode }}" class="text-decoration-none">
              {{ $t('registration') }}
            </RouterLink>
            <RouterLink :to="{ name: 'password-recovery' }" class="text-decoration-none">
              {{ $t('passReset') }}
            </RouterLink>
          </VRow>
        </template>
      </VCol>
    </VRow>
  </div>
</template>

<script>
import { getHostUrl } from '@/functions';
import IntegrationsError from '@/components/Integrations/IntegrationsError';
import Company from '@/orm/models/Company/Company';
import Locale from '@/locale';
import ValidationMixin from '../mixins/ValidationMixin';
import PageTitle from '../components/PageTitle.vue';
import User from '../orm/models/User/User';

export default {
  name: 'Login',
  metaInfo() {
    return {
      title: this.$t('login'),
    };
  },
  components: { IntegrationsError, PageTitle },
  mixins: [ValidationMixin],
  data: () => ({
    login: null,
    password: null,
    rememberMe: true,
    referralCode: null,
  }),
  computed: {
    isDev() {
      return process.env.NODE_ENV === 'development';
    },
    authUrl() {
      return `${process.env.VUE_APP_API_URL}/integrations/rabotaru/link?redirect=${getHostUrl()}/integrations`;
    },
    isIntegrationError() {
      return this.$route.query.type === 'error';
    },
    wasRedirectedToRu() {
      return !!this.$route.query.movedToRuEmail;
    },
    user() {
      return User.getAuthUser();
    },
  },
  created() {
    this.referralCode = this.$route.query.referralCode;
    if (this.$route.query.movedToRuEmail) {
      this.login = this.$route.query.movedToRuEmail;
    }
  },
  methods: {
    async auth() {
      this.errorMessage = null;
      this.errors = {};
      this.loading = true;

      try {
        try {
          await User.api().login(this.login, this.password, this.rememberMe);
        } catch (e) {
          const isApiError = e.name === 'ApiError';
          const is422 = e.code === 422;
          const hasMovedToRu = e.responseData?.data?.movedToRu === true;

          if (isApiError && is422 && hasMovedToRu) {
            const params = new URLSearchParams();
            if (this.login) {
              params.append('movedToRuEmail', this.login);
            }
            const queryString = params.toString();
            const redirectUrl = queryString
              ? `https://cabinet.huntica.io/login?${queryString}`
              : 'https://cabinet.huntica.io/login';
            console.log('Redirecting to:', redirectUrl);
            window.location.replace(redirectUrl);
            return;
          }
          throw e;
        }

        await this.withValidationHandling(async () => {
          await User.api().fetch();
          Locale.change(this.user.settings.locale);
          await Company.api().fetch();
        });

        if (this.$route.query.redirect) {
          await this.$router.push(this.$route.query.redirect);
        } else {
          await this.$router.push({ name: 'dashboard' });
        }
      } catch (e) {
        if (e.name === 'ApiError') {
          this.errorMessage = e.message;
          this.errors = e.errors;
        } else {
          throw e;
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<i18n locale="en" lang="yaml">
  login: Login
  employersEntrance: Employer sign in
  email: E-mail
  pass: Password
  rememberMe: Remember me
  registration: Registration
  passReset: Reset password
  siteMovedToRu: The site has been moved to the new domain Huntica.io
  autoRedirectedMessage: You have been automatically redirected because the Huntica.works site now operates in the .io domain zone
</i18n>

<i18n locale="ru" lang="yaml">
  login: Вход в систему
  employersEntrance: Вход для работодателей
  email: E-mail
  pass: Пароль
  rememberMe: Запомнить меня
  registration: Регистрация
  passReset: Восстановить пароль
  siteMovedToRu: Сайт перенесён на новый домен Huntica.io
  autoRedirectedMessage: Вы были автоматически перенаправлены, поскольку сайт Huntica.works теперь работает в доменной зоне .io
</i18n>
